import React, { useState, useEffect } from "react";
import { RefreshCcw } from "lucide-react";
import AddUserModal from "./AddUserModal";
import { getUserTableColumns } from "./userTableColumns";
import { Button, message } from "antd";
import ReusableModal from "src/components/reusable/ConfirmationModal";
import ResetPasswordModal from "src/layouts/AdminPortal/AdminUsersPage/ResetPasswordModal";
import StyledTable from "../../../components/reusable/StyledTable";
import EditUserModal from "src/layouts/AdminPortal/AdminUsersPage/EditUsers";
import ViewUserModal from "src/layouts/AdminPortal/AdminUsersPage/ViewUserDetails";
import {
  getTenants,
  getAllUsers,
  performUserAction,
} from "src/services/AdminPortalAPIs";
import {
  getHandleSuspend,
  getHandleDelete,
  handleAddUserSubmit as submitNewUser,
  handleResetPassword as resetUserPwd,
} from "./HandlerUsers";
import StyledInput from "src/components/reusable/StyledInput";
import StyledDropdown from "src/components/reusable/StyledDropdown";

export default function Users() {
  const [users, setUsers] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState("All");
  const [uniqueClients, setUniqueClients] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedPlan, setSelectedPlan] = useState("All");
  const [resetPasswordUser, setResetPasswordUser] = useState<any | null>(null);
  const [viewUser, setViewUser] = useState<any | null>(null);
  const [editUser, setEditUser] = useState<any | null>(null);
  const didFetchRef = React.useRef(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [tenantOptions, setTenantOptions] = useState<
    { tenant_name: string; tenant_email: string }[]
  >([]);
  const [modalData, setModalData] = useState({
    title: "",
    description: "",
    icon: "",
    confirmText: "",
    cancelText: "Cancel",
    onConfirm: () => {},
  });

  const filteredUsers = users.filter(user => {
    const clientMatch =
      selectedClient === "All" || user.client === selectedClient;
    const statusMatch =
      selectedStatus === "All" || user.status === selectedStatus;
    const planMatch = selectedPlan === "All" || user.plan === selectedPlan;

    const searchMatch =
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());

    return clientMatch && statusMatch && planMatch && searchMatch;
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await getAllUsers();
      const data = res.data;

      const mapped = data.map((u: any) => ({
        user_id: u.user_id,
        name: u.user_name,
        email: u.email,
        role: u.user_role,
        client: u.tenant_name,
        signupDate: (() => { const d = new Date(u.signup_date); return `${String(d.getDate()).padStart(2,"0")}-${String(d.getMonth()+1).padStart(2,"0")}-${d.getFullYear()}`; })(),
        plan: u.plan ?? "N/A",
        status: u.status,
        lastLogin: u.last_login === "-" ? "-" : (() => { const d = new Date(u.last_login); return `${String(d.getDate()).padStart(2,"0")}-${String(d.getMonth()+1).padStart(2,"0")}-${d.getFullYear()}`; })(),
      }));

      setUsers(mapped);

      // Extract unique client names
      const clients = Array.from(new Set(mapped.map(u => u.client))).filter(
        Boolean
      );
      setUniqueClients(clients);
    } catch (err) {
      console.error("Failed to fetch users", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (didFetchRef.current) {return;}
    didFetchRef.current = true;
    fetchUsers();
  }, []);

  const handleSuspend = (user: any) =>
    getHandleSuspend(user, setModalData, setModalOpen, fetchUsers);
  const handleDelete = (user: any) =>
    getHandleDelete(user, setModalData, setModalOpen, fetchUsers);

  const handleAddUserClick = async () => {
    try {
      const res = await getTenants();
      if (res?.data?.status) {
        setTenantOptions(res.data.data);
        setAddUserModalOpen(true);
      } else {
        message.error("Failed to load tenants.");
      }
    } catch (err) {
      message.error("Error loading tenants.");
    }
  };

  const handleAddUserSubmit = (values: any) =>
    submitNewUser(values, setAddUserModalOpen, fetchUsers);

  const handleUpdateUser = async (updatedData: any) => {
    if (!editUser) {return;}
    const isSuspending = updatedData.status === "Suspended";
    const payload = {
      user_id: editUser.user_id,
      user_name: updatedData.name,
      status: updatedData.status,
      delete: false,
      suspend: isSuspending,
    };
    try {
      const res = await performUserAction(payload);
      if (res?.data?.status) {
        message.success("User updated successfully");
        await fetchUsers();
      } else {
        message.error(res?.data?.message || "Failed to update user");
      }
    } catch {
      message.error("Error updating user");
    } finally {
      setEditUser(null);
    }
  };

  const handleResetPassword = (user: any) => setResetPasswordUser(user);

  const userTableColumns = getUserTableColumns({
    setViewUser,
    setEditUser,
    handleResetPassword,
    handleSuspend,
    handleDelete,
  });

  return (
    <div className="admin-portal-page">
      <div className="user-management-header">
        <span className="heading-primary">User Management</span>
        <div className="add-user-button-wrapper">
          <Button
            className="button button--primary"
            type="primary"
            onClick={handleAddUserClick}
          >
            + Add User
          </Button>
        </div>
      </div>

      <div className="user-management-card">
        <div className="filters">
          <StyledInput
            placeholder="Search users..."
            className="filter-input admin-search"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />

          <StyledDropdown
            value={selectedStatus}
            onChange={setSelectedStatus}
            className="filter-select"
            popupClassName="custom-dropdown-menu"
            options={[
              { value: "All", label: "All Status" },
              { value: "Active", label: "Active" },
              { value: "Inactive", label: "Inactive" },
            ]}
          />

          <StyledDropdown
            value={selectedPlan}
            onChange={setSelectedPlan}
            className="filter-select"
            popupClassName="custom-dropdown-menu"
            options={[
              { value: "All", label: "All Plans" },
              { value: "Basic", label: "Basic" },
            ]}
          />

          <StyledDropdown
            value={selectedClient}
            onChange={setSelectedClient}
            className="filter-select"
            popupClassName="custom-dropdown-menu"
            options={[
              { value: "All", label: "All Clients" },
              ...uniqueClients.map(client => ({ value: client, label: client })),
            ]}
          />

          <Button
            icon={<RefreshCcw />}
            className="refresh-btn"
            onClick={fetchUsers}
          >
            Refresh
          </Button>
        </div>

        <StyledTable
          columns={userTableColumns}
          dataSource={filteredUsers}
          rowKey="email"
          loading={loading}
          pagination={{
            pageSize: 5,
            showSizeChanger: false,
          }}
          className="pipeline-table"
          rowClassName={() => "textCaption"}
        />

        <ReusableModal
          modalOpen={modalOpen}
          onCancel={() => setModalOpen(false)}
          onConfirm={modalData.onConfirm}
          title={modalData.title}
          description={modalData.description}
          icon={modalData.icon}
          confirmText={modalData.confirmText}
          cancelText={modalData.cancelText}
        />

        <AddUserModal
          open={addUserModalOpen}
          onClose={() => setAddUserModalOpen(false)}
          onSubmit={handleAddUserSubmit}
          tenantOptions={tenantOptions}
        />

        {viewUser && (
          <ViewUserModal
            visible={!!viewUser}
            onClose={() => setViewUser(null)}
            user={viewUser}
          />
        )}

        <EditUserModal
          open={!!editUser}
          onClose={() => setEditUser(null)}
          user={editUser}
          onSubmit={handleUpdateUser}
        />

        {resetPasswordUser && (
          <ResetPasswordModal
            visible={!!resetPasswordUser}
            onCancel={() => setResetPasswordUser(null)}
            onConfirm={async () => {
              try {
                await resetUserPwd(resetPasswordUser.email);
              } catch {
                message.error("Failed to reset password");
              } finally {
                setResetPasswordUser(null);
              }
            }}
            userEmail={resetPasswordUser.email}
            userName={resetPasswordUser.name}
          />
        )}
      </div>
    </div>
  );
}
